/**
 * 글의 **부정 근거** — 카드·표·상세·엑셀이 같은 하나를 읽는다. 근거는 `brandsMention` 칸뿐이다(브랜드별 부정 문장).
 *
 * 🔑 화면이 글마다 골라 얹은 묶음(`brandNegativeEvidence`)이 배열이면 그것만 쓴다 — 빈 배열이어도 그렇다
 *   (브랜드 멘션이 «우리 브랜드 몫»만 얹는다). 얹지 않은 화면은 그 글의 모든 브랜드 몫을 쓴다.
 * ⚠️ 글 단위 `sentimentEvidenceNegative` 는 읽지 않는다 — 어느 브랜드의 부정인지 모른다.
 * ⚠️ 칸은 소급되지 않는다 — 칸이 붙기 전에 수집된 글에는 부정 근거가 없다.
 * 🔑 번역(`negativeKR`)은 묶음의 `translations` 에 원문과 같은 차례로 실린다(`negativeGroupTexts`).
 */

import type { MomentBrandMention } from 'src/types/moments/moment-domain';

/** 부정 문장을 무엇으로 보나 — 원문(`negative`) · 번역(`negativeKR`) */
export type NegativeTextMode = 'original' | 'translated';

/** 「원문 / 번역」 고르개의 칸 — 대시보드 「부정 멘션」 칸 · 소재 상세의 부정 의견이 같은 것을 쓴다 */
export const NEGATIVE_TEXT_TABS: { key: NegativeTextMode; label: string }[] = [
  { key: 'original', label: '원문' },
  { key: 'translated', label: '번역' },
];

/** 처음 보는 쪽 — 번역(번역이 없는 문장은 원문) */
export const NEGATIVE_TEXT_DEFAULT: NegativeTextMode = 'translated';

/** 화면이 얹는 칸 이름 — 값은 `NegativeMentionGroup[]` */
export const BRAND_NEGATIVE_EVIDENCE_KEY = 'brandNegativeEvidence';

/** 브랜드 하나와 그 브랜드에 대한 부정 문장들 */
export type NegativeMentionGroup = {
  /** 대표 이름(`canonicalBrand`) — 없으면 글에 적힌 표기(`brand`) */
  brand: string;
  /** 원문 */
  statements: string[];
  /** `statements` 와 같은 차례의 한국어 번역(`negativeKR`) — 번역이 없는 문장은 빈 문자열 */
  translations?: string[];
};

/**
 * `brandsMention` 칸 → 브랜드별 묶음. 같은 브랜드의 문장은 한데 묶고 겹치는 문장은 한 번.
 * 🔑 `negative`·`negativeKR` 는 색인에 **배열**로 온다 — 같은 차례끼리 짝이다(문자열 하나도 받는다).
 * @param keep 칸을 남길지 — 없으면 전부
 * @returns 처음 나온 브랜드 차례
 */
export function groupBrandMentionNegatives(
  post: unknown,
  keep?: (row: MomentBrandMention) => boolean,
): NegativeMentionGroup[] {
  const groups = new Map<string, NegativeMentionGroup & { translations: string[] }>();
  readBrandMentions(post).forEach((row) => {
    const originals = toTextList(row.negative);
    if (!originals.some((text) => text.trim())) return;
    if (keep && !keep(row)) return;
    const translated = toTextList(row.negativeKR);
    const brand = labelOf(row);
    const key = brand.toLowerCase();
    const group = groups.get(key) ?? { brand, statements: [], translations: [] };
    originals.forEach((original, index) => {
      if (!original.trim() || group.statements.includes(original)) return;
      group.statements.push(original);
      group.translations.push((translated[index] ?? '').trim());
    });
    groups.set(key, group);
  });
  return [...groups.values()];
}

/**
 * 묶음 하나의 문장들 — 번역 보기면 `negativeKR`, 번역이 없는 문장은 원문 그대로.
 * @param translated 번역으로 볼지
 */
export function negativeGroupTexts(group: NegativeMentionGroup, translated: boolean): string[] {
  if (!translated) return group.statements;
  return group.statements.map((original, index) => group.translations?.[index] || original);
}

/** 글에 번역된 부정 문장이 하나라도 있는가 */
export function hasNegativeTranslation(post: unknown): boolean {
  return readNegativeMentionGroups(post).some((group) => (group.translations ?? []).some(Boolean));
}

/** 글의 부정 근거 묶음 — 화면이 얹은 것이 있으면 그것, 없으면 글의 모든 브랜드 몫 */
export function readNegativeMentionGroups(post: unknown): NegativeMentionGroup[] {
  if (post == null || typeof post !== 'object') return [];
  const own: unknown = Object.getOwnPropertyDescriptor(post, BRAND_NEGATIVE_EVIDENCE_KEY)?.value;
  if (Array.isArray(own)) return own.filter(isGroup);
  return groupBrandMentionNegatives(post);
}

/** 본문에 빨갛게 칠할 문장들(브랜드를 떼고 펼친 것) */
export function readNegativeEvidence(post: unknown): string[] {
  return [...new Set(readNegativeMentionGroups(post).flatMap((group) => group.statements))];
}

/** 엑셀 「부정텍스트」 — `[브랜드] 문장 / [브랜드] 문장` (같은 브랜드끼리 붙어 선다) */
export function formatNegativeEvidenceText(post: unknown): string {
  return readNegativeMentionGroups(post)
    .flatMap((group) => group.statements.map((statement) => (group.brand ? `[${group.brand}] ${statement}` : statement)))
    .join(' / ');
}

function labelOf(row: MomentBrandMention): string {
  const canonical = typeof row.canonicalBrand === 'string' ? row.canonicalBrand.trim() : '';
  if (canonical) return canonical;
  return typeof row.brand === 'string' ? row.brand.trim() : '';
}

function toTextList(value: unknown): string[] {
  if (typeof value === 'string') return [value];
  if (!Array.isArray(value)) return [];
  return value.map((item) => (typeof item === 'string' ? item : ''));
}

function readBrandMentions(post: unknown): MomentBrandMention[] {
  if (post == null || typeof post !== 'object') return [];
  const list: unknown = Object.getOwnPropertyDescriptor(post, 'brandsMention')?.value;
  if (!Array.isArray(list)) return [];
  return list.filter((row): row is MomentBrandMention => row != null && typeof row === 'object');
}

function isGroup(value: unknown): value is NegativeMentionGroup {
  if (value == null || typeof value !== 'object') return false;
  const brand: unknown = Object.getOwnPropertyDescriptor(value, 'brand')?.value;
  const statements: unknown = Object.getOwnPropertyDescriptor(value, 'statements')?.value;
  return typeof brand === 'string' && Array.isArray(statements);
}
